import Button from "cms/components/Button";
import OptimizedImage from "cms/components/OptimizedImage";
import RichText from "cms/components/RichText";
import Text from "cms/components/Text";
import noiseImage from "cms/images/NoiseTexture.png";
import frontpageBannerBackgroundLightFallback from "cms/images/frontpage-banner-bg-fallback.png";
import frontpageBannerBackgroundLight from "cms/images/frontpage-banner-bg.webp";
import frontpageBannerBackgroundDarkFallback from "cms/images/purple-gradient-fallback-with-dark-transition.png";
import frontpageBannerBackgroundDark from "cms/images/purple-gradient-with-dark-transition.webp";
import { graphql } from "gatsby";
import React, { useCallback, useState } from "react";
import { Sequence, TypeAnimation } from "react-type-animation";

import { ButtonVariant } from "cms/components/Button/Button.styles";
import MediaQuery from "cms/components/MediaQuery";
import { Maybe } from "graphql/jsutils/Maybe";
import OptimizedVideo from "index";
import storage from "storage";
import {
  BackgroundImage,
  BackgroundNoise,
  ButtonContainer,
  CloseButton,
  CompaniesLogoScrollerWrapper,
  CompanyLogoScroller,
  CompanyLogoScrollerInner,
  Container,
  Content,
  HeadlineContainer,
  IconCursorContainer,
  Illustration,
  IllustrationContainer,
  Input,
  InputContainer,
  Link,
  LogoWrapper,
  LogosHeadlineContainer,
  Message,
  Placeholder,
  SearchContainer,
  TaglineContainer,
  Variant,
  VideoContainer,
  VideoFrame,
  VideoWrapper,
  YoutubeIframe,
} from "./MordorHomepageBannerBlock.styles";

export interface MordorHomepageBannerBlockProps
  extends Queries.SanityMordorHomepageBannerBlock {}

const MordorHomepageBannerBlock: React.FC<MordorHomepageBannerBlockProps> = ({
  variant,
  headline,
  _rawTagline,
  leftCursorImage,
  leftCursorImageAlt,
  rightCursorImage,
  rightCursorImageAlt,
  animatedSentences,
  heroImage,
  heroImageAlt,
  heroVideo,
  heroVideoAlt,
  button,
  logosHeadline,
  logos,
}) => {
  const [text, setText] = useState("");
  const [showVideo, setShowVideo] = React.useState(false);

  const generateSentences = useCallback(() => {
    if (!animatedSentences) {
      return [];
    }
    const sentences = animatedSentences.flatMap((sentence: Maybe<string>) => [
      sentence,
      1000,
    ]);
    return sentences as Sequence;
  }, [animatedSentences]);

  const sentencesSequence = generateSentences();

  const handleOpenVideo = () => {
    setShowVideo(true);
  };

  const handleCloseVideo = () => {
    setShowVideo(false);
  };

  const onSubmit = () => {
    storage.setItemSync("websitePrompt", text, { crossDomain: true });
  };

  return (
    <Container>
      <BackgroundImage $variant={variant as Variant}>
        <source
          type="image/webp"
          srcSet={
            variant === "light"
              ? frontpageBannerBackgroundLight
              : frontpageBannerBackgroundDark
          }
        />
        <source
          type="image/png"
          srcSet={
            variant === "light"
              ? frontpageBannerBackgroundLightFallback
              : frontpageBannerBackgroundDarkFallback
          }
        />
        <img
          src={
            variant === "light"
              ? frontpageBannerBackgroundLightFallback
              : frontpageBannerBackgroundDarkFallback
          }
          alt="Gradient background"
        />
      </BackgroundImage>
      <BackgroundNoise
        $backgroundNoise={noiseImage}
        $variant={variant as Variant}
      />
      <Content>
        <Message>
          {leftCursorImage && leftCursorImageAlt && (
            <IconCursorContainer $position={{ top: "28%", left: "-0.5%" }}>
              <OptimizedImage
                image={leftCursorImage}
                alt={leftCursorImageAlt}
              />
            </IconCursorContainer>
          )}

          {rightCursorImage && rightCursorImageAlt && (
            <IconCursorContainer $position={{ bottom: "-8%", right: "-1.5%" }}>
              <OptimizedImage
                image={rightCursorImage}
                alt={rightCursorImageAlt}
              />
            </IconCursorContainer>
          )}

          <HeadlineContainer $variant={variant as Variant}>
            <Text
              variant="D1"
              as="h1"
              color="activated"
              textAlign="center"
              weight="medium"
            >
              {headline}
            </Text>
          </HeadlineContainer>
          <TaglineContainer>
            <RichText text={_rawTagline} />
          </TaglineContainer>
        </Message>

        <SearchContainer $variant={variant as Variant}>
          <InputContainer>
            <Input
              type="text"
              name="generate"
              value={text}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setText(event.target.value)
              }
            />
            <Placeholder $hasInput={!!text.trim()}>
              {animatedSentences && (
                <TypeAnimation
                  sequence={sentencesSequence}
                  speed={50}
                  repeat={Infinity}
                />
              )}
            </Placeholder>
          </InputContainer>
          <ButtonContainer>
            {button?.url &&
              button.label &&
              button.buttonIconImage &&
              button.buttonIconImageAlt && (
                <Link href={button.url}>
                  <Button
                    label={button.label}
                    variant={button.variant as ButtonVariant}
                    icon={button.buttonIconImage}
                    iconAlt={button.buttonIconImageAlt}
                    size="large"
                    fullWidth
                    onClick={onSubmit}
                  />
                </Link>
              )}
          </ButtonContainer>
        </SearchContainer>

        <IllustrationContainer $variant={variant as Variant}>
          <Illustration $variant={variant as Variant}>
            {heroVideo && (
              <VideoWrapper onClick={handleOpenVideo}>
                <OptimizedVideo video={heroVideo} alt={heroVideoAlt} />
              </VideoWrapper>
            )}
            {!heroVideo && heroImage && (
              <OptimizedImage image={heroImage} alt={heroImageAlt} />
            )}
          </Illustration>
        </IllustrationContainer>

        {showVideo && heroVideo && (
          <VideoContainer>
            <CloseButton
              onClick={handleCloseVideo}
              style={{ position: "absolute", top: "0", right: "0" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 12 12"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line
                  x1="1"
                  y1="11"
                  x2="11"
                  y2="1"
                  stroke="white"
                  strokeWidth="2"
                />
                <line
                  x1="1"
                  y1="1"
                  x2="11"
                  y2="11"
                  stroke="white"
                  strokeWidth="2"
                />
              </svg>
            </CloseButton>

            <VideoFrame>
              <MediaQuery $lessThan="medium" style={{ width: "100%" }}>
                <YoutubeIframe
                  width={Math.round(
                    window.innerWidth - window.innerWidth * 0.2,
                  )}
                  height={Math.round(
                    (window.innerWidth - window.innerWidth * 0.2) / (1 / 1),
                  )}
                  src="https://www.youtube.com/embed/_jP1Ki4b6cs?si=Z8Gl-fwoWSlQXFet"
                  title="The Autodesigner 2.0 Update is Here!"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  $allowfullscreen
                />
              </MediaQuery>
              <MediaQuery $greaterThan="medium" style={{ width: "100%" }}>
                <YoutubeIframe
                  width={Math.round(
                    window.innerWidth - window.innerWidth * 0.2,
                  )}
                  height={Math.round(
                    (window.innerWidth - window.innerWidth * 0.2) / (16 / 9),
                  )}
                  src="https://www.youtube.com/embed/_jP1Ki4b6cs?si=Z8Gl-fwoWSlQXFet"
                  title="The Autodesigner 2.0 Update is Here!"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  $allowfullscreen
                />
              </MediaQuery>
            </VideoFrame>
          </VideoContainer>
        )}

        {logosHeadline && logos && (
          <CompaniesLogoScrollerWrapper>
            <LogosHeadlineContainer>
              <Text
                variant="H5"
                as="h5"
                textAlign="center"
                weight="medium"
                color="#FFF"
              >
                {logosHeadline}
              </Text>
            </LogosHeadlineContainer>
            <CompanyLogoScroller>
              <CompanyLogoScrollerInner>
                {logos?.map((logo, index) => (
                  <LogoWrapper key={index} aria-hidden="true">
                    {logo?.logoImage && (
                      <OptimizedImage
                        image={logo?.logoImage}
                        alt={logo?.logoImageAlt}
                      />
                    )}
                  </LogoWrapper>
                ))}
              </CompanyLogoScrollerInner>

              <CompanyLogoScrollerInner aria-hidden="true">
                {logos?.map((logo, index) => (
                  <LogoWrapper key={index} aria-hidden="true">
                    {logo?.logoImage && (
                      <OptimizedImage
                        image={logo?.logoImage}
                        alt={logo?.logoImageAlt}
                      />
                    )}
                  </LogoWrapper>
                ))}
              </CompanyLogoScrollerInner>
            </CompanyLogoScroller>
          </CompaniesLogoScrollerWrapper>
        )}
      </Content>
    </Container>
  );
};

export const query = graphql`
  fragment MordorHomepageBannerBlock on SanityMordorHomepageBannerBlock {
    _key
    _type
    variant
    headline
    _rawTagline(resolveReferences: { maxDepth: 5 })
    leftCursorImage { ...OptimizedImage }
    leftCursorImageAlt
    rightCursorImage { ...OptimizedImage }
    rightCursorImageAlt
    animatedSentences
    heroImage { ...OptimizedImage }
    heroImageAlt
    heroVideo { ...OptimizedVideo }
    heroVideoAlt
    button {
      variant
      label
      url
      buttonIconImage { ...OptimizedImage }
      buttonIconImageAlt
    }
    logosHeadline
    logos {
      logoImage { ...OptimizedImage }
      logoImageAlt
    }
  }
`;

export default MordorHomepageBannerBlock;
